import { useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import Autocomplete from "@/components/Autocomplete";
import Icon from "@/components/Icon";
import { GET_FACILITIES } from "@/pages/billing/queries";

interface FacilityOption {
  id: string;
  name: string;
  addresses: {
    address1: string;
    city: string;
    state: string;
    postalCode: string;
  }[];
}

interface FacilitiesQueryData {
  facilities: {
    nodes: FacilityOption[];
  };
}

interface FacilityAutocompleteProps {
  facilityName?: string;
  onSelect: (name: string) => void;
  onClear: () => void;
}

export default function FacilityAutocomplete({ facilityName, onSelect, onClear }: FacilityAutocompleteProps) {
  const [searchText, setSearchText] = useState(facilityName || "");
  const [error, setError] = useState<string | undefined>();
  const skipNextFacilitySyncRef = useRef(false);

  useEffect(() => {
    if (skipNextFacilitySyncRef.current) {
      skipNextFacilitySyncRef.current = false;
      return;
    }

    setSearchText(facilityName || "");
    setError(undefined);
  }, [facilityName]);

  const { data, loading } = useQuery<FacilitiesQueryData>(GET_FACILITIES, {
    variables: {
      input: searchText ? { search: searchText } : {}
    }
  });

  function handleSelect(facility: FacilityOption) {
    setSearchText(facility.name);
    setError(undefined);
    onSelect(facility.name);
  }

  function handleReset() {
    setSearchText("");
    setError(undefined);
    onClear();
  }

  function handleChangeText(text: string) {
    setSearchText(text);
    setError(undefined);
    if (facilityName && text !== facilityName) {
      skipNextFacilitySyncRef.current = true;
      onClear();
    }
  }

  function handleBlur() {
    if (searchText && !facilityName) {
      setError("Please select a valid facility");
      return;
    }

    if (facilityName && searchText !== facilityName) {
      setSearchText(facilityName);
    }
  }

  return (
    <Autocomplete
      variant="searchableDropdown"
      label="Facility"
      data-testid="grp-enrollment-facility-autocomplete"
      query={searchText}
      data={data?.facilities?.nodes ?? []}
      loadingResults={loading}
      menuClassName="max-h-[20rem]"
      onChangeText={handleChangeText}
      onBlur={handleBlur}
      onSelect={handleSelect}
      reset={handleReset}
      error={!!error}
      errorText={error}
      onMouseDown={(e: MouseEvent) => {
        e.preventDefault();
      }}
      renderItem={(facility) => (
        <div className="flex items-start border-bottom">
          <div className="bg-sys-brand-secondary-container rounded-full p-2 mr-4 flex items-center justify-center">
            <Icon name="location_on" size={24} />
          </div>
          <div>
            <div className="text-body-large">{facility.name}</div>
            {facility.addresses[0] && (
              <div className="text-body-medium text-sys-brand-on-surface-varian">
                {facility.addresses[0].address1}, {facility.addresses[0].city}, {facility.addresses[0].state}
              </div>
            )}
          </div>
        </div>
      )}
    />
  );
}
